import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Header } from '../components/Header';
import { SkipLink } from '../components/SkipLink';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, TrendingUp, Users, Clock, CheckCircle, Download, FileText, List, Sparkles, Target, Flame, Brain, BookOpen } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { format } from 'date-fns';
import { QuestionAnalytics } from '../components/QuestionAnalytics';
import { FormHealthScore } from '../components/FormHealthScore';
import { SubmissionStatus } from '../components/SubmissionStatus';
import { AIInsightsPanel } from '../components/AIInsightsPanel';
import { LeadScoreBadge } from '../components/LeadScoreBadge';
import { InterviewHealthPanel } from '../components/InterviewHealthPanel';
import { EfficiencyMetrics } from '../components/EfficiencyMetrics';
import { OptimizationInsightsPanel } from '../components/OptimizationInsightsPanel';
import { QualificationPanel } from '../components/QualificationPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const QRAnalytics = () => {
  const { qrId } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [qrCode, setQrCode] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [trends, setTrends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDays, setSelectedDays] = useState(7);
  const [leadFilter, setLeadFilter] = useState('all');
  const [expandedQualification, setExpandedQualification] = useState({});

  useEffect(() => {
    fetchData();
  }, [qrId, selectedDays, leadFilter]);

  const fetchData = async () => {
    try {
      const [qrResponse, analyticsResponse, submissionsResponse, trendsResponse] = await Promise.all([
        axios.get(`${API}/qr-codes/${qrId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/qr-codes/${qrId}/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/qr-codes/${qrId}/submissions-detail${leadFilter !== 'all' ? `?label=${leadFilter}` : ''}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/qr-codes/${qrId}/trends?days=${selectedDays}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setQrCode(qrResponse.data);
      setAnalytics(analyticsResponse.data);
      setSubmissions(submissionsResponse.data.submissions || submissionsResponse.data);
      setTrends(trendsResponse.data);
    } catch (error) {
      toast.error('Failed to load analytics');
      navigate('/qr-codes');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const exportToCSV = async () => {
    if (submissions.length === 0) {
      toast.error('No submissions to export');
      return;
    }
    try {
      const res = await axios.get(`${API}/qr-codes/${qrId}/submissions/export/csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${qrCode?.title?.replace(/\s+/g, '-') || 'responses'}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV downloaded!');
    } catch {
      toast.error('Failed to export CSV');
    }
  };

  const downloadPDF = async () => {
    try {
      const response = await axios.get(`${API}/qr-codes/${qrId}/report`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${qrCode?.title.replace(/\s+/g, '-')}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('PDF report downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <SkipLink />
        <Header />
        <main id="main-content" className="flex items-center justify-center min-h-screen">
          <div className="text-center" role="status" aria-live="polite">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1d4ed8] mx-auto" aria-hidden="true"></div>
            <p className="mt-4 text-[#475569]">Loading analytics...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <SkipLink />
      <Header />
      
      <main id="main-content" className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Button
              variant="ghost"
              onClick={() => navigate('/qr-codes')}
              className="mb-4 rounded-full"
              aria-label="Back to QR codes"
            >
              <ArrowLeft className="h-4 w-4 mr-2" aria-hidden="true" />
              Back to QR Codes
            </Button>
            
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-3xl sm:text-4xl font-black mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {qrCode?.title}
                </h1>
                {qrCode?.description && (
                  <p className="text-[#475569]">{qrCode.description}</p>
                )}
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => navigate(`/qr-codes/${qrId}/responses`)}
                  disabled={submissions.length === 0}
                  variant="outline"
                  className="rounded-full"
                  aria-label="View detailed responses"
                >
                  <List className="h-4 w-4 mr-2" aria-hidden="true" />
                  View Responses
                </Button>
                <Button
                  onClick={downloadPDF}
                  variant="outline"
                  className="rounded-full"
                  aria-label="Download PDF report"
                >
                  <FileText className="h-4 w-4 mr-2" aria-hidden="true" />
                  PDF Report
                </Button>
                <Button
                  onClick={exportToCSV}
                  disabled={submissions.length === 0}
                  className="rounded-full bg-[#1d4ed8] hover:bg-[#1e40af]"
                  aria-label="Export submissions to CSV"
                >
                  <Download className="h-4 w-4 mr-2" aria-hidden="true" />
                  Export CSV
                </Button>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0 }}
            >
              <Card className="p-6 border border-[#E2E8F0]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#475569]">Total Views</span>
                  <Users className="h-5 w-5 text-[#1d4ed8]" aria-hidden="true" />
                </div>
                <p className="text-3xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {analytics?.total_views || 0}
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="p-6 border border-[#E2E8F0]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#475569]">Submissions</span>
                  <CheckCircle className="h-5 w-5 text-green-600" aria-hidden="true" />
                </div>
                <p className="text-3xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {analytics?.total_submissions || 0}
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-6 border border-[#E2E8F0]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#475569]">Completion Rate</span>
                  <TrendingUp className="h-5 w-5 text-[#1d4ed8]" aria-hidden="true" />
                </div>
                <p className="text-3xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {analytics?.completion_rate || 0}%
                </p>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="p-6 border border-[#E2E8F0]">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-[#475569]">Avg Time</span>
                  <Clock className="h-5 w-5 text-[#1d4ed8]" aria-hidden="true" />
                </div>
                <p className="text-3xl font-black" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {analytics?.avg_completion_time ? `${Math.round(analytics.avg_completion_time)}s` : 'N/A'}
                </p>
              </Card>
            </motion.div>
          </div>

          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <Card className="p-6 border border-[#E2E8F0]">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Views & Submissions
                </h2>
                <select
                  value={selectedDays}
                  onChange={(e) => setSelectedDays(Number(e.target.value))}
                  className="text-sm border border-[#E2E8F0] rounded-lg px-3 py-1"
                  aria-label="Select time range"
                >
                  <option value={7}>Last 7 days</option>
                  <option value={14}>Last 14 days</option>
                  <option value={30}>Last 30 days</option>
                </select>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => format(new Date(date), 'MMM d')}
                    stroke="#475569"
                  />
                  <YAxis stroke="#475569" />
                  <Tooltip 
                    labelFormatter={(date) => format(new Date(date), 'MMM d, yyyy')}
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #E2E8F0' }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="views" 
                    stroke="#1d4ed8" 
                    strokeWidth={2}
                    name="Views"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="submissions" 
                    stroke="#16a34a" 
                    strokeWidth={2}
                    name="Submissions"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Card>

            <Card className="p-6 border border-[#E2E8F0]">
              <h2 className="text-xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Completion Rate
              </h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={trends}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="date" 
                    tickFormatter={(date) => format(new Date(date), 'MMM d')}
                    stroke="#475569"
                  />
                  <YAxis stroke="#475569" />
                  <Tooltip 
                    labelFormatter={(date) => format(new Date(date), 'MMM d, yyyy')}
                    contentStyle={{ backgroundColor: 'white', border: '1px solid #E2E8F0' }}
                    formatter={(value) => `${value}%`}
                  />
                  <Legend />
                  <Bar 
                    dataKey="completion_rate" 
                    fill="#1d4ed8" 
                    name="Completion Rate (%)"
                  />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          {/* Interview Health — AI Question Optimization */}
          <div className="mb-8">
            <InterviewHealthPanel qrId={qrId} />
          </div>

          {/* Interview Efficiency Metrics */}
          <div className="mb-8">
            <EfficiencyMetrics qrId={qrId} />
          </div>

          {/* AI Optimization Insights */}
          <div className="mb-8">
            <OptimizationInsightsPanel qrId={qrId} />
          </div>

          {/* Per-Question Analytics */}
          <div className="mb-8">
            <QuestionAnalytics qrId={qrId} />
          </div>

          {/* AI Form Health Score */}
          <div className="mb-8">
            <FormHealthScore qrId={qrId} />
          </div>

          {/* AI Insights */}
          <div className="mb-8">
            <AIInsightsPanel qrId={qrId} />
          </div>

          {/* Submission Status Dashboard */}
          <div className="mb-8">
            <SubmissionStatus qrId={qrId} />
          </div>

          {/* Recent Submissions */}
          <section aria-labelledby="submissions-heading">
            <div className="flex items-center justify-between mb-6">
              <h2
                id="submissions-heading"
                className="text-2xl font-bold"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                Recent Submissions
              </h2>
              <div className="flex gap-1.5" data-testid="lead-filter-buttons">
                {[
                  { value: 'all', label: 'All', color: 'bg-[#F1F5F9] text-[#334155]' },
                  { value: 'hot', label: 'Hot', color: 'bg-red-50 text-red-700 border-red-200' },
                  { value: 'warm', label: 'Warm', color: 'bg-amber-50 text-amber-700 border-amber-200' },
                  { value: 'cold', label: 'Cold', color: 'bg-blue-50 text-blue-700 border-blue-200' },
                ].map(f => (
                  <button
                    key={f.value}
                    onClick={() => setLeadFilter(f.value)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-all font-medium ${
                      leadFilter === f.value
                        ? f.color + ' ring-2 ring-offset-1 ring-[#1d4ed8]'
                        : 'bg-white text-[#475569] border-[#E2E8F0] hover:border-[#CBD5E1]'
                    }`}
                    data-testid={`lead-filter-${f.value}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>
            
            {submissions.length === 0 ? (
              <Card className="p-12 text-center border border-[#E2E8F0]">
                <CheckCircle className="h-12 w-12 text-[#475569] mx-auto mb-4" aria-hidden="true" />
                <p className="text-[#475569]">No submissions yet. Share your QR code to start collecting responses!</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {submissions.slice(0, 10).map((submission, index) => (
                  <motion.div
                    key={submission.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card className="p-6 border border-[#E2E8F0] hover:border-[#1d4ed8] transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <CheckCircle className="h-5 w-5 text-green-600" aria-hidden="true" />
                            <span className="font-medium">Submission #{submissions.length - index}</span>
                            {submission.lead_label && submission.lead_label !== 'none' && (
                              <LeadScoreBadge score={submission.lead_score} label={submission.lead_label} />
                            )}
                            <button
                              onClick={() => setExpandedQualification(prev => ({
                                ...prev,
                                [submission.id]: !prev[submission.id],
                              }))}
                              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border border-[#1d4ed8] text-[#1d4ed8] hover:bg-[#EFF6FF] transition-colors focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]"
                              aria-expanded={!!expandedQualification[submission.id]}
                              data-testid={`qualification-toggle-${index}`}
                            >
                              <Target className="h-3 w-3" aria-hidden="true" />
                              {expandedQualification[submission.id] ? 'Hide' : 'Qualification'}
                            </button>
                          </div>
                          {submission.ai_summary && (
                            <div className="mb-2 p-2 rounded-md bg-[#EFF6FF] border border-[#BFDBFE]">
                              <div className="flex items-center gap-1.5 mb-1">
                                <Sparkles className="h-3.5 w-3.5 text-[#1d4ed8]" />
                                <span className="text-xs font-medium text-[#1d4ed8]">AI Summary</span>
                              </div>
                              <p className="text-xs text-[#334155]">{submission.ai_summary}</p>
                            </div>
                          )}
                          {submission.lead_explanation && submission.lead_label !== 'none' && (
                            <p className="text-xs text-[#475569] mb-2 italic">{submission.lead_explanation}</p>
                          )}
                          {submission.interview_metrics && (
                            <div className="flex flex-wrap gap-2 mb-2">
                              {submission.interview_metrics.interview_depth != null && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-700">
                                  <Brain className="h-3 w-3" />
                                  Depth: {Math.round(submission.interview_metrics.interview_depth * 100)}%
                                </span>
                              )}
                              {submission.interview_metrics.avg_signal_strength != null && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                                  Signal: {Math.round(submission.interview_metrics.avg_signal_strength * 100)}%
                                </span>
                              )}
                              {submission.interview_metrics.fields_extracted > 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                                  {submission.interview_metrics.fields_extracted} extracted
                                </span>
                              )}
                              {submission.interview_metrics.questions_skipped > 0 && (
                                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">
                                  {submission.interview_metrics.questions_skipped} skipped
                                </span>
                              )}
                            </div>
                          )}
                          {submission.playbook_extractions && Object.keys(submission.playbook_extractions).length > 0 && (
                            <div className="mb-2 p-2 rounded-md bg-indigo-50 border border-indigo-200">
                              <div className="flex items-center gap-1.5 mb-1">
                                <BookOpen className="h-3.5 w-3.5 text-indigo-600" />
                                <span className="text-xs font-medium text-indigo-700">Playbook Extractions</span>
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-1">
                                {Object.entries(submission.playbook_extractions).map(([key, val]) => (
                                  <span key={key} className="text-xs text-[#334155]">
                                    <span className="font-medium">{key}:</span> {typeof val === 'object' ? val.value : val}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                              <span className="text-[#475569]">Completed:</span>
                              <p className="font-medium">
                                {new Date(submission.completed_at).toLocaleDateString()} at{' '}
                                {new Date(submission.completed_at).toLocaleTimeString()}
                              </p>
                            </div>
                            <div>
                              <span className="text-[#475569]">Time taken:</span>
                              <p className="font-medium">{formatTime(submission.completion_time_seconds)}</p>
                            </div>
                          </div>
                          {Object.keys(submission.responses).length > 0 && (
                            <div className="mt-3 pt-3 border-t border-[#E2E8F0]">
                              <span className="text-sm text-[#475569]">Responses:</span>
                              <div className="mt-2 space-y-1">
                                {Object.entries(submission.responses).slice(0, 3).map(([key, value]) => (
                                  <p key={key} className="text-sm">
                                    <span className="font-medium">{key}:</span> {String(value)}
                                  </p>
                                ))}
                                {Object.keys(submission.responses).length > 3 && (
                                  <p className="text-sm text-[#475569]">
                                    +{Object.keys(submission.responses).length - 3} more responses
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      {expandedQualification[submission.id] && (
                        <div className="mt-4 pt-4 border-t border-[#E2E8F0]" data-testid={`qualification-section-${index}`}>
                          <QualificationPanel qrId={qrId} sessionId={submission.session_id || submission.id} />
                        </div>
                      )}
                    </Card>
                  </motion.div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};
